import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import { CheckCircle, ArrowRight, Apple } from 'lucide-react';

const PostpartumRecoveryPage = () => {
  const inclusions = [
    "Recovery-focused, doctor-guided nutrition plan",
    "Breastfeeding-friendly meal planning",
    "Gradual fitness re-introduction with safety oversight",
    "Practical guidance for sleep, hydration and energy",
    "WhatsApp-based ongoing support"
  ];

  const forWhom = [
    "Mothers within 12 months postpartum",
    "Mothers exclusively or partially breastfeeding",
    "Mothers easing back into movement and fitness",
    "Mothers managing postpartum nutrition concerns"
  ];

  const faqs = [
    { q: "When can I start the postpartum program?", a: "Most mothers can begin after their initial postpartum check-up clears them medically. We work alongside your physician's clearance." },
    { q: "Is the plan breastfeeding-friendly?", a: "Yes. Our nutrition guidance considers breastfeeding needs and never compromises maternal or infant nutrition for rapid weight loss." },
    { q: "Will the plan help with weight management?", a: "We focus on safe, gradual, sustainable changes. Postpartum recovery is the priority, and weight changes happen naturally with consistency." },
    { q: "Is the entire program online?", a: "Yes. Everything is delivered online, including consultations, plans, and follow-ups." }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Postpartum Recovery Program: Doctor-Guided Nutrition | TCUBE FITNESS™</title>
        <meta name="description" content="Doctor-guided postpartum recovery program with breastfeeding-friendly nutrition and gradual return to fitness, fully online." />
        <link rel="canonical" href="https://tcubefitness.com/postpartum-recovery" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Postpartum Recovery Program"
        description="Doctor-guided postpartum recovery with breastfeeding-friendly nutrition and a gradual, safe return to fitness, fully online."
        url="/postpartum-recovery"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Postpartum Recovery', path: '/postpartum-recovery' },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" className="pt-32 sm:pt-36 pb-24 lg:pb-16">
        <div className="container mx-auto px-4 sm:px-6">

          <section className="max-w-4xl mx-auto text-center mb-14">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-4">
              <Apple className="w-4 h-4" aria-hidden="true" />
              Postpartum Care
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Postpartum <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Recovery Program</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              Recovery-focused, breastfeeding-friendly nutrition and gradual fitness re-introduction, supervised by healthcare professionals.
            </p>
            <Link to="/book-consultation?from=postpartum-top" className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white px-7 py-3.5 rounded-full font-bold shadow-lg shadow-emerald-500/25" data-testid="postpartum-cta-top">
              Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </section>

          <section className="max-w-3xl mx-auto mb-12 bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Program Overview</h2>
            <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
              The postpartum window is a sensitive recovery period. Our program emphasizes restoration, balanced nutrition, breastfeeding support, and gradual movement, designed in line with sound medical principles.
            </p>
          </section>

          <section className="max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 text-center">Who It's For</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {forWhom.map((p, i) => (
                <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" aria-hidden="true" /> {p}
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 text-center">Program Inclusions</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {inclusions.map((it, i) => (
                <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" aria-hidden="true" /> {it}
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-5" data-testid={`postpartum-faq-${i}`}>
                  <summary className="text-white font-semibold cursor-pointer text-sm sm:text-base">{f.q}</summary>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="max-w-3xl mx-auto text-center bg-gradient-to-br from-emerald-900/30 to-green-900/30 border border-emerald-500/30 rounded-3xl p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Begin Your Recovery Journey</h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">A free consultation will help us understand your needs and recommend the right next steps.</p>
            <Link to="/book-consultation?from=postpartum-bottom" className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white px-7 py-3.5 rounded-full font-bold shadow-lg shadow-emerald-500/25" data-testid="postpartum-cta-bottom">
              Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </section>

        </div>
      </main>

      <ProgramFramework slug="postpartum-recovery" />
      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default PostpartumRecoveryPage;
